// Contrato de la pantalla Mensajes al cliente (Admin SureKeep): tipos + constantes de UI.
// Cuatro canales de entrega: SMS, push y MDM operan; WhatsApp se anuncia y todavía no. Correo no.
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';

/** Los canales que la pantalla PINTA. No hay correo; WhatsApp se ve pero todavía no opera. */
export type MessageChannel = 'sms' | 'push' | 'mdm' | 'whatsapp';

/**
 * El orden en el que se pintan los canales: es el del recorrido del mensaje, no alfabético.
 * Lo que todavía no opera va al final, después de los tres que sí.
 */
export const MESSAGE_CHANNELS: readonly MessageChannel[] = ['sms', 'push', 'mdm', 'whatsapp'];

/**
 * Los canales que la pantalla prende, apaga y guarda. El backend no conoce ningún otro: lo que
 * queda fuera de este tipo no puede entrar al borrador ni al payload, y no compila si lo intenta.
 */
export type OperableChannel = Exclude<MessageChannel, 'whatsapp'>;

export const OPERABLE_CHANNELS: readonly OperableChannel[] = ['sms', 'push', 'mdm'];

/** El rótulo del canal que se anuncia y todavía no se puede tocar. */
export const UPCOMING_CHANNEL_BADGE = 'Próximamente';

/** Si el canal ya opera. El que no, se pinta como letrero «Próximamente» y no es un control. */
export function isOperableChannel(channel: MessageChannel): channel is OperableChannel {
    return (OPERABLE_CHANNELS as readonly MessageChannel[]).includes(channel);
}

/**
 * Los canales que llevan copy editable y fila propia en messages-business. MDM no: elige del
 * catálogo. WhatsApp tampoco: no opera, y meterlo aquí lo pondría en el payload del guardado.
 */
export type CopyChannel = Exclude<OperableChannel, 'mdm'>;

export const COPY_CHANNELS: readonly CopyChannel[] = ['sms', 'push'];

export const CHANNEL_LABEL: Record<MessageChannel, string> = {
    sms: 'SMS',
    push: 'Push in-app',
    mdm: 'Aviso MDM',
    whatsapp: 'WhatsApp',
};

/** Cuántos segmentos se cobran a lo más por SMS: SNS parte los largos y cobra cada parte. */
export const SMS_MAX_SEGMENTS = 3;
/** Lo que cabe en un SMS de un solo segmento, en GSM-7 y en UCS-2. */
export const SMS_SINGLE_SEGMENT_GSM7 = 160;
export const SMS_SINGLE_SEGMENT_UCS2 = 70;
/** Lo que cabe en cada parte de un SMS partido: la cabecera de la concatenación se come el resto. */
export const SMS_SEGMENT_GSM7 = 153;
export const SMS_SEGMENT_UCS2 = 67;
/** El tope del copy de SMS: tres partes, 459 en GSM-7 y 201 en UCS-2. */
export const SMS_LIMIT_GSM7 = SMS_MAX_SEGMENTS * SMS_SEGMENT_GSM7;
export const SMS_LIMIT_UNICODE = SMS_MAX_SEGMENTS * SMS_SEGMENT_UCS2;

/** El título de la tarjeta del editor: dice el canal y su restricción de una vez. */
export const CHANNEL_HINT: Record<MessageChannel, string> = {
    sms: `SMS · hasta ${String(SMS_MAX_SEGMENTS)} SMS: ${String(SMS_LIMIT_GSM7)} caracteres GSM-7, ${String(SMS_LIMIT_UNICODE)} si el texto trae acentos o emoji fuera del set`,
    push: 'Push in-app · corto y accionable',
    mdm: 'Aviso MDM · lo muestra el equipo bloqueado, con un texto fijo del catálogo',
    whatsapp: `WhatsApp · ${UPCOMING_CHANNEL_BADGE.toLowerCase()}; el canal todavía no se puede prender ni editar`,
};

/** Tope base de caracteres. El de SMS depende del texto: lo resuelve `channelLimit` en logic.ts. */
export const CHANNEL_LIMIT: Record<CopyChannel, number> = { sms: SMS_LIMIT_GSM7, push: 80 };

/** La inicial que lleva el badge del árbol, donde no cabe el nombre del canal. */
export const CHANNEL_BADGE: Record<MessageChannel, string> = {
    sms: 'S',
    push: 'P',
    mdm: 'M',
    whatsapp: 'W',
};

/** Relleno pastel del badge y del ícono de cada canal. */
export const CHANNEL_TONE: Record<MessageChannel, string> = {
    sms: 'bg-info-soft text-info',
    push: 'bg-warning-soft text-warning',
    mdm: 'bg-danger-soft text-danger',
    // Apagado a propósito: el canal se anuncia, no se opera, y un pastel vivo lo haría ver activo.
    whatsapp: 'bg-muted text-muted-foreground',
};

/**
 * Los avisos del equipo bloqueado, espejo de `MdmNoticeTypeEnum`. Se declaran aquí porque el
 * type-kit instalado todavía no lo exporta y la pantalla necesita sus etiquetas en español.
 */
export type MdmNoticeType =
    | 'PRE_DUE_2_DAYS'
    | 'PRE_DUE_1_DAY'
    | 'DUE_TODAY'
    | 'OVERDUE_1_DAY'
    | 'OVERDUE_3_DAYS'
    | 'OVERDUE_7_DAYS'
    | 'OVERDUE_14_DAYS'
    | 'OVERDUE_25_DAYS'
    | 'OVERDUE_30_DAYS'
    | 'OVERDUE_60_DAYS';

/** Que el admin no haya elegido aviso es un estado legítimo, no un error de captura. */
export const NO_MDM_NOTICE = '';

export type MdmNoticeChoice = MdmNoticeType | typeof NO_MDM_NOTICE;

/** Los avisos EN ORDEN temporal, del previo al vencimiento al más atrasado. */
export const MDM_NOTICE_TYPES: readonly MdmNoticeType[] = [
    'PRE_DUE_2_DAYS',
    'PRE_DUE_1_DAY',
    'DUE_TODAY',
    'OVERDUE_1_DAY',
    'OVERDUE_3_DAYS',
    'OVERDUE_7_DAYS',
    'OVERDUE_14_DAYS',
    'OVERDUE_25_DAYS',
    'OVERDUE_30_DAYS',
    'OVERDUE_60_DAYS',
];

export const MDM_NOTICE_LABEL: Record<MdmNoticeType, string> = {
    PRE_DUE_2_DAYS: '2 días antes del vencimiento',
    PRE_DUE_1_DAY: '1 día antes del vencimiento',
    DUE_TODAY: 'Vence hoy',
    OVERDUE_1_DAY: '1 día de atraso',
    OVERDUE_3_DAYS: '3 días de atraso',
    OVERDUE_7_DAYS: '7 días de atraso',
    OVERDUE_14_DAYS: '14 días de atraso',
    OVERDUE_25_DAYS: '25 días de atraso',
    OVERDUE_30_DAYS: '30 días de atraso',
    OVERDUE_60_DAYS: '60 días de atraso',
};

export type MessageBlockId =
    | 'origination'
    | 'reminders'
    | 'payments'
    | 'collections'
    | 'deviceLock'
    | 'exceptions'
    | 'closure'
    | 'service'
    | 'referrals';

export interface MessageBlock {
    blockId: MessageBlockId;
    /** El número del bloque, que es el que pinta el cuadro de color del árbol. */
    number: number;
    name: string;
    description: string;
    /** Relleno pastel del cuadro numerado. */
    toneClass: string;
}

/**
 * Los bloques del ciclo de vida del crédito, EN ORDEN de recorrido y no alfabético: el número
 * del bloque es parte de su nombre y ordenarlos por letra rompería la lectura del ciclo.
 */
export const MESSAGE_BLOCKS: readonly MessageBlock[] = [
    {
        blockId: 'origination',
        number: 1,
        name: 'Originación',
        description: 'Bienvenida y activación del crédito',
        toneClass: 'bg-success-soft text-success',
    },
    {
        blockId: 'reminders',
        number: 2,
        name: 'Recordatorios pago',
        description: 'Antes de vencer (T-3, T-2, T-1)',
        toneClass: 'bg-info-soft text-info',
    },
    {
        blockId: 'payments',
        number: 3,
        name: 'Confirmación pagos',
        description: 'Aplicado, parcial, rechazado, adelantado',
        toneClass: 'bg-success-soft text-success',
    },
    {
        blockId: 'collections',
        number: 4,
        name: 'Cobranza escalada',
        description: 'Desde el vencimiento (T → T+60)',
        toneClass: 'bg-warning-soft text-warning',
    },
    {
        blockId: 'deviceLock',
        number: 5,
        name: 'MDM / bloqueo',
        description: 'Bloqueo y liberación del equipo',
        toneClass: 'bg-danger-soft text-danger',
    },
    {
        blockId: 'exceptions',
        number: 6,
        name: 'Excepciones',
        description: 'F1-F5 y compromisos',
        toneClass: 'bg-info-soft text-info',
    },
    {
        blockId: 'closure',
        number: 7,
        name: 'Fin del crédito',
        description: 'Última cuota, liquidación, reingreso',
        toneClass: 'bg-tier-gold-soft text-tier-gold',
    },
    {
        blockId: 'service',
        number: 8,
        name: 'Servicio + Regulatorio',
        description: 'Bienvenida app, OTP, avisos legales',
        toneClass: 'bg-muted text-muted-foreground',
    },
    {
        blockId: 'referrals',
        number: 9,
        name: 'Referidos',
        description: 'Premio abonado y código de descuento',
        toneClass: 'bg-success-soft text-success',
    },
];

/** Bloques cuyos momentos salen solo por SMS: loan-config rechaza cualquier otro canal. */
export const SMS_ONLY_BLOCKS: readonly MessageBlockId[] = ['referrals'];

export const SMS_ONLY = 'Este momento sale solo por SMS.';

/** El bloque que arranca abierto: llegar a un árbol todo cerrado no muestra nada. */
export const DEFAULT_OPEN_BLOCK: MessageBlockId = 'collections';

export type TriggerRule =
    | 'at_event'
    | 't_minus_3'
    | 't_minus_2'
    | 't_minus_1'
    | 't_zero'
    | 't_plus_1'
    | 't_plus_3'
    | 't_plus_14'
    | 't_plus_60'
    | 'day_5_post_settlement'
    | 'day_30_post_settlement'
    | 'monthly_day_1'
    | 'manual';

/** Las reglas EN ORDEN temporal: es una línea de tiempo, ordenarlas por letra la deshace. */
export const TRIGGER_RULE_LABEL: Record<TriggerRule, string> = {
    at_event: 'Al momento del evento (inmediato)',
    t_minus_3: 'T-3 · 3 días antes del vencimiento',
    t_minus_2: 'T-2 · 2 días antes del vencimiento',
    t_minus_1: 'T-1 · 1 día antes del vencimiento',
    t_zero: 'T · el día del vencimiento',
    t_plus_1: 'T+1 · 1 día después del vencimiento',
    t_plus_3: 'T+3 · 3 días después del vencimiento',
    t_plus_14: 'T+14 · 14 días después del vencimiento',
    t_plus_60: 'T+60 · 60 días después del vencimiento',
    day_5_post_settlement: 'Día +5 después de liquidar el crédito',
    day_30_post_settlement: 'Día +30 después de liquidar el crédito',
    monthly_day_1: 'Día 1 de cada mes (corte)',
    manual: 'Manual (Admin lo dispara desde el flujo)',
};

/**
 * El día del vencimiento de cada regla de CALENDARIO. Las que no están aquí disparan por evento
 * (`at_event`, `manual`, los post-liquidación y el corte mensual) y no tienen día que elegir:
 * cambiar un momento de evento a calendario no es un dropdown, son dos caminos distintos del motor.
 */
export const TRIGGER_RULE_DAY_OFFSET: Partial<Record<TriggerRule, number>> = {
    t_minus_3: -3,
    t_minus_2: -2,
    t_minus_1: -1,
    t_zero: 0,
    t_plus_1: 1,
    t_plus_3: 3,
    t_plus_14: 14,
    t_plus_60: 60,
};

/** El día de una regla de calendario; `Number.NaN` en las de evento, que no tienen día. */
function dayOffsetOf(rule: TriggerRule): number {
    return TRIGGER_RULE_DAY_OFFSET[rule] ?? Number.NaN;
}

/**
 * Las reglas que SÍ se pueden elegir, ordenadas por su día y no por el orden en que se declaran:
 * el dropdown es una línea de tiempo y el día del vencimiento va entre los negativos y los positivos.
 */
export const CALENDAR_TRIGGER_RULES: readonly TriggerRule[] = (
    Object.keys(TRIGGER_RULE_DAY_OFFSET) as TriggerRule[]
).sort((left, right) => dayOffsetOf(left) - dayOffsetOf(right));

/** Del día declarado por la ficha a su regla; `undefined` si ningún renglón usa ese día. */
export function triggerRuleForDayOffset(dayOffset: number): TriggerRule | undefined {
    return CALENDAR_TRIGGER_RULES.find((rule) => TRIGGER_RULE_DAY_OFFSET[rule] === dayOffset);
}

/** Si el momento dispara por calendario y por lo tanto su día se puede elegir. */
export function isCalendarTrigger(rule: TriggerRule): boolean {
    return TRIGGER_RULE_DAY_OFFSET[rule] !== undefined;
}

export type MessagePriority = 'high' | 'medium' | 'low';

/** La prioridad es una escala, no un catálogo: va de mayor a menor y no en orden alfabético. */
export const MESSAGE_PRIORITIES: readonly MessagePriority[] = ['high', 'medium', 'low'];

export const PRIORITY_LABEL: Record<MessagePriority, string> = {
    high: 'Alta',
    medium: 'Media',
    low: 'Baja',
};

/** Los niveles a los que puede aplicar una plantilla. Mismo catálogo que Segmentos. */
export const MESSAGE_TIERS: readonly ClientLevelEnum[] = [
    ClientLevelEnum.GOLD,
    ClientLevelEnum.SILVER,
    ClientLevelEnum.BRONZE,
];

export interface MessageChannelCopy {
    enabled: boolean;
    /** El copy editable del canal. En MDM va siempre vacío: su texto sale del catálogo. */
    copy: string;
    /** Solo push: el título. SMS y MDM no lo llevan. */
    title?: string;
}

/**
 * La regla de disparo. `rule`, `timeWindow` y `maxSends` viven en los parámetros F1 de
 * loan-config-business y la pantalla solo los muestra; `priority` y `tiers` son de la ficha.
 */
export interface MessageTrigger {
    rule: TriggerRule;
    /** Ventana horaria de envío, ya formateada por el parámetro (`08:00 – 20:00`). */
    timeWindow: string;
    maxSends: number;
    priority: MessagePriority;
    tiers: ClientLevelEnum[];
}

export interface MessageLogEntry {
    /** Día de calendario `YYYY-MM-DD`; se pinta con `formatDayMx`. */
    day: string;
    author: string;
    change: string;
}

export interface MessageTemplate {
    /** Id de la plantilla (`PM-001`) — también el parámetro del deep link `?p=`. */
    templateId: string;
    blockId: MessageBlockId;
    /** Evento del motor que la dispara. Es una clave técnica, no copy. */
    event: string;
    name: string;
    active: boolean;
    /** Regulatoria: sus canales y su aviso MDM no se cambian desde la pantalla; su texto sí. */
    regulatory: boolean;
    /** Solo los canales operables: lo que no opera no tiene estado que guardar. */
    channels: Record<OperableChannel, MessageChannelCopy>;
    /** El aviso del equipo bloqueado. Vacío mientras la ficha no traiga uno del catálogo. */
    noticeType: MdmNoticeChoice;
    trigger: MessageTrigger;
    /** Las variables que el emisor manda en este momento; fuera de ellas el texto no se guarda. */
    variables: readonly MomentVariable[];
}

/** Un bloque del árbol ya filtrado: el bloque, sus plantillas visibles y si va abierto. */
export interface MessageTreeBlock {
    block: MessageBlock;
    templates: MessageTemplate[];
    open: boolean;
}

/** Una variable que el emisor manda en un momento. La condicional puede llegar vacía. */
export interface MomentVariable {
    name: string;
    conditional: boolean;
}

/** Datos ficticios con los que se dibuja la vista previa. Nunca salen de la pantalla. */
export const PREVIEW_CONTEXT: Record<string, string> = {
    campo_cambiado: 'teléfono',
    cat: '245',
    cliente_nombre: 'María Sánchez',
    code: '482913',
    codigo: 'MARIA-7K2Q',
    credito_id: 'CR-1234',
    cuota_n: '5',
    cuota_nueva: '$365',
    cuota_total: '12',
    descripcion_correccion: 'cargo duplicado revertido',
    descuento: '$200',
    dias_atraso: '3',
    dispositivo: 'iPhone 14 · CDMX',
    equipo: 'Samsung Galaxy A25',
    fecha_corte: '31/jul/2026',
    fecha_liquidacion: '19/jul/2026',
    fecha_primer_venc: '28/jul',
    fecha_promesa: '16/jul',
    fecha_venc: '14/jul',
    folio: 'FCF-12547',
    forma_pago: 'tarjeta',
    monto: '$487',
    monto_credito: '$5,850',
    monto_descuento: '$150',
    monto_devolucion: '$1,650',
    monto_enganche: '$1,750',
    otp: '482913',
    plazo_nuevo: '16',
    proxima_venc: '21/jul',
    referido_nombre: 'Juan Pérez',
    saldo_actual: '$3,890',
    saldo_nuevo: '$4,215',
    saldo_pendiente: '$243',
    telefono_contacto: '55 8000 3200',
    tienda: 'Neza Centro',
    vigencia: '30/nov/2026',
};

/** Las tres pestañas del editor. */
export type MessageEditorTab = 'message' | 'trigger' | 'history';

export const MESSAGE_EDITOR_TABS: readonly { key: MessageEditorTab; label: string }[] = [
    { key: 'message', label: 'Mensaje' },
    { key: 'trigger', label: 'Disparo' },
    { key: 'history', label: 'Historial' },
];
